/**
 * Utilitários para verificação de roles e permissões
 * Baseado na especificação em roles.md
 */

// Roles válidos no sistema
export const VALID_ROLES = [
  'admin',
  'supervisor',
  'planner',
  'clinic',
  'doctor',
  'printer',
  'partner',
  'instructor'
]

// Categorias de roles
export const ADMIN_ROLES = ['admin', 'supervisor']
export const INTERNAL_ROLES = ['admin', 'supervisor', 'planner', 'printer']
export const EXTERNAL_ROLES = ['clinic', 'doctor', 'partner']
export const SPECIAL_ROLES = ['instructor']

// Roles de planejamento
export const PLANNER_ROLES = ['admin', 'supervisor', 'planner']

/**
 * Valida se um role é válido
 * @param {string} role - Role a ser validado
 * @returns {boolean}
 */
export const isValidRole = (role) => {
  return VALID_ROLES.includes(role)
}

/**
 * Valida se um array de roles contém apenas roles válidos
 * @param {string[]} roles - Array de roles a serem validados
 * @returns {boolean}
 */
export const areValidRoles = (roles) => {
  if (!Array.isArray(roles)) return false
  return roles.every(role => isValidRole(role))
}

/**
 * Verifica se o usuário tem pelo menos um dos roles requeridos
 * @param {string[]} userRoles - Roles do usuário
 * @param {string[]} requiredRoles - Roles requeridos (OR logic)
 * @returns {boolean}
 */
export const hasRequiredRoles = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  if (!requiredRoles || !Array.isArray(requiredRoles) || requiredRoles.length === 0) return true
  
  // Admin sempre tem acesso
  if (userRoles.includes('admin')) return true
  
  // Verifica se tem pelo menos um dos roles requeridos
  return requiredRoles.some(role => userRoles.includes(role))
}

/**
 * Verifica se o usuário tem todos os roles requeridos
 * @param {string[]} userRoles - Roles do usuário
 * @param {string[]} requiredRoles - Roles requeridos (AND logic)
 * @returns {boolean}
 */
export const hasAllRequiredRoles = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  if (!requiredRoles || !Array.isArray(requiredRoles) || requiredRoles.length === 0) return true
  
  // Admin sempre tem acesso
  if (userRoles.includes('admin')) return true
  
  return requiredRoles.every(role => userRoles.includes(role))
}

/**
 * Verifica se o usuário é admin
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isAdmin = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin')
}

/**
 * Verifica se o usuário é admin ou supervisor
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isSupervisor = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('supervisor')
}

/**
 * Verifica se o usuário é admin, supervisor ou planner
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isPlanner = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('supervisor') || userRoles.includes('planner')
}

/**
 * Verifica se o usuário é admin ou clinic
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isClinic = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('clinic')
}

/**
 * Verifica se o usuário é admin, clinic ou doctor
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isDoctor = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('clinic') || userRoles.includes('doctor')
}

/**
 * Verifica se o usuário é admin ou printer
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isPrinter = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('printer')
}

/**
 * Verifica se o usuário é admin ou partner
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isPartner = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('partner')
}

/**
 * Verifica se o usuário é admin ou instructor
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const isInstructor = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.includes('admin') || userRoles.includes('instructor')
}

/**
 * Verifica se o role pertence à categoria ADMIN_ROLES
 * @param {string} role - Role a ser verificado
 * @returns {boolean}
 */
export const isAdminRole = (role) => {
  return ADMIN_ROLES.includes(role)
}

/**
 * Verifica se o role pertence à categoria INTERNAL_ROLES
 * @param {string} role - Role a ser verificado
 * @returns {boolean}
 */
export const isInternalRole = (role) => {
  return INTERNAL_ROLES.includes(role)
}

/**
 * Verifica se o role pertence à categoria EXTERNAL_ROLES
 * @param {string} role - Role a ser verificado
 * @returns {boolean}
 */
export const isExternalRole = (role) => {
  return EXTERNAL_ROLES.includes(role)
}

/**
 * Verifica se o role pertence à categoria SPECIAL_ROLES
 * @param {string} role - Role a ser verificado
 * @returns {boolean}
 */
export const isSpecialRole = (role) => {
  return SPECIAL_ROLES.includes(role)
}

/**
 * Verifica se o usuário tem pelo menos um role da categoria ADMIN_ROLES
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const hasAdminRole = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.some(role => ADMIN_ROLES.includes(role))
}

/**
 * Verifica se o usuário tem pelo menos um role da categoria INTERNAL_ROLES
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const hasInternalRole = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.some(role => INTERNAL_ROLES.includes(role))
}

/**
 * Verifica se o usuário tem pelo menos um role da categoria EXTERNAL_ROLES
 * @param {string[]} userRoles - Roles do usuário
 * @returns {boolean}
 */
export const hasExternalRole = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.some(role => EXTERNAL_ROLES.includes(role))
}

/**
 * Retorna o role principal (mais alto na hierarquia)
 * @param {string[]} userRoles - Roles do usuário
 * @returns {string|null}
 */
export const getMainRole = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) return null
  
  const roleOrder = [
    'admin',
    'supervisor',
    'planner',
    'clinic',
    'doctor',
    'printer',
    'partner',
    'instructor'
  ]
  
  for (const role of roleOrder) {
    if (userRoles.includes(role)) {
      return role
    }
  }
  
  return userRoles[0] || null
}

